import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Divider,
  Typography,
} from '@mui/material';
import { WatchlistGroup } from '../../types';

interface GroupSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (groupId: string | null, newGroupName?: string) => void | Promise<void>;
  title: string;
  groups: WatchlistGroup[];
  stockName?: string;
  stockSymbol?: string;
  showUngroupedOption?: boolean;
  confirmButtonText?: string;
}

const GroupSelectorDialog: React.FC<GroupSelectorDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  groups,
  stockName,
  stockSymbol,
  showUngroupedOption = true,
  confirmButtonText = '確定',
}) => {
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [newGroupName, setNewGroupName] = useState('');

  const handleClose = () => {
    setSelectedGroup('');
    setNewGroupName('');
    onClose();
  };

  const handleConfirm = async () => {
    // CLEAN-02:等待非同步 onConfirm 完成後才關閉對話框 —
    // 舊版不等待即 handleClose,失敗時對話框已關閉、操作看似成功
    try {
      if (newGroupName.trim()) {
        // 創建新分組
        await onConfirm(null, newGroupName.trim());
      } else if (selectedGroup) {
        // 選擇現有分組
        const groupId = selectedGroup === 'ungrouped' ? null : selectedGroup;
        await onConfirm(groupId);
      }
      handleClose();
    } catch {
      // 失敗時保留對話框讓使用者重試
    }
  };

  const isValid = selectedGroup || newGroupName.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {stockName && stockSymbol && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                {stockSymbol}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stockName}
              </Typography>
            </Box>
          )}

          <FormControl fullWidth>
            <InputLabel>選擇分組</InputLabel>
            <Select
              value={selectedGroup}
              label="選擇分組"
              onChange={(e) => setSelectedGroup(e.target.value)}
            >
              {showUngroupedOption && (
                <MenuItem value="ungrouped">未分類</MenuItem>
              )}
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: group.color,
                      }}
                    />
                    {group.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }}>或</Divider>

          <TextField
            fullWidth
            label="建立新分組"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="輸入新分組名稱..."
            helperText="建立新分組並將股票加入其中"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>取消</Button>
        <Button 
          variant="contained" 
          onClick={handleConfirm}
          disabled={!isValid}
        >
          {newGroupName.trim() ? '建立分組' : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupSelectorDialog;
